import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import About from "@/components/About";
import Services from "@/components/Services";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PageBackdrop } from "@/components/PageBackdrop";

const WORKSPACE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3f7ef5a3-e08e-4053-bc90-55479d570c9f/images/a7ca51f1dbba74250b8d7335e0e015a23c16b7e6064b1aaac294e22549f43990.jpeg";

const faqs = [
  {
    q: "What does a project look like?",
    a: "We start with a short call about your business and goals. You get a fixed quote and a clear timeline, then I build in stages you can review. No big reveal at the end.",
  },
  {
    q: "How much does a website cost?",
    a: "Every project is priced individually after we've talked. You always get a fixed quote up front, so you know the full cost before any work starts.",
  },
  {
    q: "How long does it take?",
    a: "Most small business sites go from brief to launch in a few weeks. Bigger builds take longer. Your quote includes a realistic timeline, not an optimistic one.",
  },
  {
    q: "Can I update the site myself?",
    a: "Yes, you'll get a site that's straightforward to edit. And if you'd rather not touch it, care plans cover updates, backups and small fixes for you.",
  },
  {
    q: "What do you need from me to start?",
    a: "A few lines about your business, what the site needs to do, and anything you already have. Logo, photos, copy. If you don't have content yet, I'll help you put it together.",
  },
  {
    q: "What happens after launch?",
    a: "I stay around. Updates, backups, small fixes and straight advice are all part of it. The person who built your site is the person looking after it.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

const FaqItem = ({ item, index, open, onToggle }) => (
  <div className="overflow-hidden rounded-2xl bg-white shadow-ambient" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      data-testid={`faq-toggle-${index}`}
      aria-expanded={open}
      className="flex w-full items-center justify-between gap-4 px-7 py-5 text-left transition-colors duration-300 hover:bg-paper2/50"
    >
      <span className="font-display text-lg font-medium text-ink sm:text-xl">{item.q}</span>
      <Plus
        className={`h-5 w-5 shrink-0 text-jade transition-transform duration-300 ${
          open ? "rotate-45" : ""
        }`}
      />
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="px-7 pb-6 leading-relaxed text-ink2">{item.a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const TABS = ["Story", "Services", "Questions"];

const AboutPage = () => {
  usePageMeta("Vagus Digital | About the studio");
  const [tab, setTab] = useState("Story");
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <section className="relative min-h-screen overflow-hidden bg-paper pb-24 pt-32 lg:pt-40">
      <PageBackdrop src="/art/about.webp" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          {...fadeUp}
          transition={{ duration: 0.6 }}
          className="text-xs font-medium uppercase tracking-[0.3em] text-jade"
        >
          About
        </motion.p>
        <motion.h1
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.08 }}
          className="mt-4 font-display text-3xl font-light leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl"
          data-testid="about-page-heading"
        >
          The studio, <em className="italic text-jade">in brief</em>.
        </motion.h1>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-10 inline-flex flex-wrap gap-2 rounded-full border border-jade/15 bg-white p-1.5 shadow-ambient"
          data-testid="about-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`about-tab-${t.toLowerCase()}`}
              aria-pressed={tab === t}
              className={`rounded-full px-6 py-2 text-sm font-medium transition-colors duration-300 ${
                tab === t ? "bg-jade text-paper" : "text-ink2 hover:text-jade"
              }`}
            >
              {t}
            </button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`about-panel-${tab.toLowerCase()}`}
          >
            {tab === "Story" && (
              <div className="pt-12">
                <About embedded />
                <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-12">
                  <div className="overflow-hidden rounded-2xl shadow-ambient md:col-span-8">
                    <img
                      src={WORKSPACE_IMG}
                      alt="Workspace flat-lay with laptop, jade teacup, bamboo and enso sketch on rice paper"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex items-center rounded-2xl bg-white p-10 shadow-ambient md:col-span-4">
                    <p className="font-display text-2xl font-light italic leading-snug text-ink">
                      "Design is not decoration. It's how much trust a first
                      glance can carry."
                    </p>
                  </div>
                </div>
              </div>
            )}

            {tab === "Services" && (
              <div className="pt-12 [&>section]:!border-0 [&>section]:!bg-transparent [&>section]:!py-0 [&>section]:!px-0">
                <Services />
              </div>
            )}

            {tab === "Questions" && (
              <div className="mx-auto max-w-3xl space-y-4 pt-12" data-testid="faq-section">
                {faqs.map((item, i) => (
                  <FaqItem
                    key={item.q}
                    item={item}
                    index={i}
                    open={openFaq === i}
                    onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                  />
                ))}
                <div className="flex justify-center pt-8">
                  <Link
                    to="/contact"
                    data-testid="about-cta-link"
                    className="inline-flex items-center gap-2 rounded-full bg-jade px-8 py-3.5 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink"
                  >
                    Start a project <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default AboutPage;